import { request } from "$/lib/api"
import {
  AllBookableServiceSlots,
  BookableServiceSlots,
  Route,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

async function fetch(storeId: string): Promise<AllBookableServiceSlots> {
  return await request<AllBookableServiceSlots>({
    url: populateRoute(Route.BOOKING_RULES__GET_BOOKABLE_SLOTS, storeId),
    method: "GET",
  })
}

async function upsert(storeId: string, data: BookableServiceSlots) {
  return await request<void>({
    url: populateRoute(Route.BOOKING_RULES__UPSERT_BOOKABLE_SLOTS, storeId),
    method: "PATCH",
    data,
  })
}

export function useBookableServiceSlotsMutation(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries(
      bookableServiceSlotsQueries.all(storeId),
    )
  }

  const upsertMutation = useMutation({
    mutationFn: (data: BookableServiceSlots) => upsert(storeId, data),
    onSettled: invalidateQueries,
  })

  return { upsertMutation }
}

const bookableServiceSlotsQueries = {
  all: (storeId: string) =>
    queryOptions({
      queryKey: ["bookable-service-slots", storeId],
      queryFn: () => fetch(storeId),
      // Store slot configuration rarely changes and is invalidated on upsert;
      // a long staleTime keeps the calendar's blocking load off the network.
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    }),
}

export default bookableServiceSlotsQueries
